// src/components/common/toastMessage.ts

import { createElement, getElementByIdSafe } from '../../utils/domHelpers';
import { ToastOptions } from '../../types/components';
import * as bootstrap from 'bootstrap';

// Store references to active toast instances for cleanup
const activeToasts: { element: HTMLElement; instance: bootstrap.Toast }[] = [];

/**
 * Safely cleans up all active toast instances before creating a new one
 */
export function dismissActiveToasts(): void {
    // Make a copy of the array since we'll be modifying it during iteration
    const toastsToCleanup = [...activeToasts];
    
    toastsToCleanup.forEach(toast => {
        try {
            // First check if the element still exists in the DOM
            if (document.body.contains(toast.element)) {
                toast.instance.hide();
                // Don't remove the element here - let the hidden event handler do it
            } else {
                // Element is already gone, just remove from tracking array
                const index = activeToasts.indexOf(toast);
                if (index > -1) {
                    activeToasts.splice(index, 1);
                }
            }
        } catch (error) {
            console.warn("Error cleaning up toast:", error);
            // Still try to remove the element if it exists
            if (document.body.contains(toast.element)) {
                toast.element.remove();
            }
            // Remove from tracking array
            const index = activeToasts.indexOf(toast);
            if (index > -1) {
                activeToasts.splice(index, 1);
            }
        }
    });
}

/**
 * Displays a Bootstrap 5 toast message at the bottom center of the screen.
 * Removes any existing toasts before showing the new one.
 *
 * @param options - Configuration for the toast message.
 * @param options.message - The HTML or text message to display.
 * @param options.background - Optional Bootstrap background class (e.g., 'text-bg-primary', 'text-bg-danger'). Defaults to 'text-bg-primary'.
 * @param options.delay - Optional auto-hide delay in milliseconds (default: 5000). Set to 0 for no auto-hide.
 * @param options.bottomOffset - Optional offset in pixels from the bottom of the screen.
 */
export function messageToast(options: ToastOptions): void {
    // Clean up existing toasts first
    dismissActiveToasts();

    const {
        message,
        background = 'text-bg-primary', // Default background
        delay = 5000, // Default delay
        bottomOffset = 0,
        placement = 'bottom',
        topOffset = 0,
    } = options;

    const positionStyle =
        placement === 'top'
            ? {
                  top: `${16 + topOffset}px`,
                  bottom: 'auto',
              }
            : {
                  bottom: `${30 + bottomOffset}px`,
                  top: 'auto',
              };

    const toastElement = createElement('div', {
        // Add a specific class 'ace-toast' for easier targeting
        className: `toast ace-toast align-items-center ${background} border-0`, // Added border-0 for cleaner look
        attributes: {
            role: 'alert',
            'aria-live': 'assertive',
            'aria-atomic': 'true',
            ...(delay > 0 && { 'data-bs-delay': String(delay) }) // Add delay attribute only if > 0
        },
        style: {
            position: 'fixed',
            ...positionStyle,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: '9999', // High z-index
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)' // Add shadow for visibility
        }
    });

    const dFlex = createElement('div', { className: 'd-flex' }, toastElement);

    createElement('div', {
        className: 'toast-body',
        innerHTML: message // Allow HTML in message
    }, dFlex);

    const closeButton = createElement('button', {
        className: 'btn-close me-2 m-auto', // Ensure white close button for contrast
        attributes: {
            type: 'button',
            'data-bs-dismiss': 'toast',
            'aria-label': 'Close'
        }
    }, dFlex);

    document.body.appendChild(toastElement);

    try {
        // Bootstrap requires `delay` to be a number when provided; omit it when
        // autohide is off (delay === 0) so sticky toasts (e.g. survey CTA) work.
        const toastInstance = new bootstrap.Toast(toastElement, {
            autohide: delay > 0,
            ...(delay > 0 ? { delay } : {}),
        });

        // Add to active toasts tracking array
        activeToasts.push({ element: toastElement, instance: toastInstance });

        // Ensure cleanup after hide
        toastElement.addEventListener('hidden.bs.toast', () => {
            // First remove from tracking array
            const index = activeToasts.findIndex(toast => toast.element === toastElement);
            if (index > -1) {
                activeToasts.splice(index, 1);
            }
            
            // Then check if element still exists in DOM before removing
            if (document.body.contains(toastElement)) {
                toastElement.remove(); // Remove element from DOM
            }
        });

        // Manual close button click - add extra safety
        closeButton.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent event bubbling
            
            // Find in tracking array and remove
            const index = activeToasts.findIndex(toast => toast.element === toastElement);
            if (index > -1) {
                activeToasts.splice(index, 1);
            }
            
            // Check if element is still in DOM
            if (document.body.contains(toastElement)) {
                // Directly remove without animation to avoid Bootstrap errors
                toastElement.remove();
            }
        });

        toastInstance.show();
    } catch (error) {
        console.error("Failed to initialize Bootstrap Toast:", error);
        
        // Clean up if toast initialization failed
        if (document.body.contains(toastElement)) {
            toastElement.remove();
        }
        
        // Remove from tracking array if needed
        const index = activeToasts.findIndex(toast => toast.element === toastElement);
        if (index > -1) {
            activeToasts.splice(index, 1);
        }
    }
}


/**
 * Renders a generic error message within a specified container.
 * Used by global error handlers.
 * @param containerId - The ID of the container element (defaults to 'content').
 * @param message - Custom error message (defaults to 'Something went wrong').
 */
export function renderErrorMessage(containerId: string = 'content', message: string = 'Something went wrong'): void {
    const container = getElementByIdSafe(containerId);
    if (container) {
        container.innerHTML = `<div class="d-flex justify-content-center align-items-center vh-100 text-center text-secondary">
                                   <h3>${message}</h3>
                               </div>`;
    } else {
        // Fallback if container not found
        messageToast({ message: "Sorry, an error has occurred.", background: "text-bg-danger" });
    }
}